import { KaazaTheme } from '../types/theme';

const shared = {
  fontFamily: "'Montserrat', sans-serif",

  fontSize: {
    xs: '12px',
    sm: '13px',
    base: '14px',
    md: '16px',
    lg: '18px',
    xl: '22px',
    '2xl': '24px',
    '3xl': '1.8rem',
  },

  fontWeight: {
    light: 300,
    normal: 400,
    medium: 500,
    semiBold: 600,
    bold: 700,
    black: 900,
  },

  spacing: {
    1: '4px',
    2: '8px',
    3: '12px',
    4: '16px',
    5: '20px',
    6: '24px',
    8: '32px',
    10: '40px',
    12: '48px',
    page: '30px',
    listPage: '50px',
    gutter: '25px',
  },

  borderRadius: {
    sm: '2px',
    base: '4px',
    md: '8px',
    lg: '12px',
    xl: '16px',
  },

  breakpoints: {
    sm: '540px',
    md: '768px',
    lg: '961px',
  },

  transition: {
    fast: '0.15s ease',
    base: '0.2s ease',
    slow: '0.3s ease',
  },

  colors: {
    primary: {
      base: '#00286E',
      950: '#001437',
      900: '#00286E',
      700: '#003A8C',
      light: '#E6F1FF',
      auxiliaryLight: '#5b9bd5',
    },
    secondary: {
      base: '#4e446f',
    },
    neutral: {
      white: '#FFFFFF',
      50: '#F9FAFB',
      100: '#F3F4F6',
      200: '#E5E7EB',
      300: '#D1D5DB',
      400: '#9CA3AF',
      500: '#6B7280',
      600: '#4B5563',
      700: '#374151',
      800: '#1F2937',
      900: '#111827',
    },
    success: {
      base: '#52C41A',
      light: '#f6ffed',
      border: '#b7eb8f',
      hover: '#389e0d',
    },
    error: {
      base: '#ff4d4f',
      light: '#fff2f0',
      border: '#ffccc7',
      hover: '#d9363e',
      dark: '#FF3232',
    },
    warning: {
      base: '#FFBC20',
      light: '#fff7e6',
      border: '#ffd591',
      text: '#d46b08',
    },
    cards: {
      purple: '#6366f1',
      gray: '#64748b',
      green: '#10b981',
      teal: '#06b6d4',
      rose: '#f43f5e',
    },
  },
};

export const lightTheme: KaazaTheme = {
  ...shared,
  mode: 'light',

  shadows: {
    sm: '0 1px 2px rgba(0, 0, 0, 0.05)',
    base: '0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06)',
    md: '0 4px 6px rgba(0, 0, 0, 0.07), 0 2px 4px rgba(0, 0, 0, 0.05)',
    lg: '0 10px 15px rgba(0, 0, 0, 0.1), 0 4px 6px rgba(0, 0, 0, 0.05)',
  },

  focus: {
    ring: '0 0 0 2px #ffffff, 0 0 0 4px #3b82f6',
  },

  colors: {
    ...shared.colors,
    bg: '#f4f5f7',
    surface: '#ffffff',
    surfaceHover: '#f9fafb',
    surfaceRaised: '#ffffff',
    border: '#e5e7eb',
    borderHover: '#d1d5db',
    text: {
      primary: '#111827',
      secondary: '#4b5563',
      muted: '#9ca3af',
      inverse: '#ffffff',
    },
    sidebar: {
      bg: '#ffffff',
      bgHover: '#f3f4f6',
      bgActive: '#eff6ff',
      border: '#e5e7eb',
      text: '#4b5563',
      textActive: '#00286E',
      accent: '#00286E',
    },
    scrollbar: {
      thumb: 'rgba(156, 163, 175, 0.5)',
      track: '#f9fafb',
    },
    overlay: 'rgba(0, 0, 0, 0.5)',
    tag: {
      green: { bg: '#f0fdf4', text: '#15803d', border: '#bbf7d0' },
      red: { bg: '#fef2f2', text: '#b91c1c', border: '#fecaca' },
      orange: { bg: '#fff7ed', text: '#c2410c', border: '#fed7aa' },
      blue: { bg: '#00286E', text: '#ffffff', border: '#00286E' },
      geekblue: { bg: '#c7d2fe', text: '#1e3a8a', border: '#818cf8' },
      cyan: { bg: '#eff6ff', text: '#3b82f6', border: '#bfdbfe' },
      purple: { bg: '#f5f3ff', text: '#6d28d9', border: '#c4b5fd' },
      volcano: { bg: '#fff1f0', text: '#cf1322', border: '#ffa39e' },
      gold: { bg: '#fffbeb', text: '#92400e', border: '#fde68a' },
      lime: { bg: '#f7fee7', text: '#3f6212', border: '#bef264' },
      magenta: { bg: '#fdf2f8', text: '#9d174d', border: '#f9a8d4' },
      pink: { bg: '#fff1f2', text: '#9f1239', border: '#fda4af' },
      teal: { bg: '#f0fdfa', text: '#115e59', border: '#99f6e4' },
      amber: { bg: '#fffbeb', text: '#78350f', border: '#fcd34d' },
    },
  },
};

export const darkTheme: KaazaTheme = {
  ...shared,
  mode: 'dark',

  shadows: {
    sm: '0 1px 2px rgba(0, 0, 0, 0.3)',
    base: '0 1px 3px rgba(0, 0, 0, 0.4), 0 1px 2px rgba(0, 0, 0, 0.3)',
    md: '0 4px 6px rgba(0, 0, 0, 0.35), 0 2px 4px rgba(0, 0, 0, 0.25)',
    lg: '0 10px 15px rgba(0, 0, 0, 0.4), 0 4px 6px rgba(0, 0, 0, 0.3)',
  },

  focus: {
    ring: '0 0 0 2px #1e2231, 0 0 0 4px #60a5fa',
  },

  colors: {
    ...shared.colors,
    primary: {
      ...shared.colors.primary,
      base: '#3b7dd8',
      900: '#3b7dd8',
      700: '#5b9bd5',
      light: 'rgba(59, 125, 216, 0.12)',
      auxiliaryLight: '#5b9bd5',
    },
    bg: '#1a1e2e',
    surface: '#242838',
    surfaceHover: '#2c3045',
    surfaceRaised: '#303550',
    border: '#3a3f54',
    borderHover: '#4a5068',
    text: {
      primary: '#eceef2',
      secondary: '#a8adb8',
      muted: '#7a8090',
      inverse: '#111827',
    },
    sidebar: {
      bg: '#1e2233',
      bgHover: '#2a2e42',
      bgActive: 'rgba(59, 125, 216, 0.15)',
      border: '#3a3f54',
      text: '#a8adb8',
      textActive: '#5b9bd5',
      accent: '#5b9bd5',
    },
    scrollbar: {
      thumb: 'rgba(120, 128, 145, 0.5)',
      track: '#242838',
    },
    overlay: 'rgba(0, 0, 0, 0.6)',
    tag: {
      green: {
        bg: 'rgba(34, 197, 94, 0.15)',
        text: '#4ade80',
        border: 'rgba(34, 197, 94, 0.3)',
      },
      red: {
        bg: 'rgba(239, 68, 68, 0.15)',
        text: '#f87171',
        border: 'rgba(239, 68, 68, 0.3)',
      },
      orange: {
        bg: 'rgba(251, 146, 60, 0.15)',
        text: '#fb923c',
        border: 'rgba(251, 146, 60, 0.3)',
      },
      blue: {
        bg: 'rgba(59, 130, 246, 0.2)',
        text: '#93c5fd',
        border: 'rgba(59, 130, 246, 0.4)',
      },
      geekblue: {
        bg: 'rgba(99, 102, 241, 0.15)',
        text: '#a5b4fc',
        border: 'rgba(99, 102, 241, 0.3)',
      },
      cyan: {
        bg: 'rgba(56, 189, 248, 0.15)',
        text: '#7dd3fc',
        border: 'rgba(56, 189, 248, 0.3)',
      },
      purple: {
        bg: 'rgba(139, 92, 246, 0.15)',
        text: '#a78bfa',
        border: 'rgba(139, 92, 246, 0.3)',
      },
      volcano: {
        bg: 'rgba(239, 68, 68, 0.15)',
        text: '#fca5a5',
        border: 'rgba(239, 68, 68, 0.3)',
      },
      gold: {
        bg: 'rgba(245, 158, 11, 0.15)',
        text: '#fbbf24',
        border: 'rgba(245, 158, 11, 0.3)',
      },
      lime: {
        bg: 'rgba(132, 204, 22, 0.15)',
        text: '#a3e635',
        border: 'rgba(132, 204, 22, 0.3)',
      },
      magenta: {
        bg: 'rgba(236, 72, 153, 0.15)',
        text: '#f472b6',
        border: 'rgba(236, 72, 153, 0.3)',
      },
      pink: {
        bg: 'rgba(251, 113, 133, 0.15)',
        text: '#fb7185',
        border: 'rgba(251, 113, 133, 0.3)',
      },
      teal: {
        bg: 'rgba(20, 184, 166, 0.15)',
        text: '#2dd4bf',
        border: 'rgba(20, 184, 166, 0.3)',
      },
      amber: {
        bg: 'rgba(245, 158, 11, 0.15)',
        text: '#fcd34d',
        border: 'rgba(245, 158, 11, 0.3)',
      },
    },
  },
};

export default lightTheme;
